import type {
  CustomInstrumentRequestStatusEnum,
  ICustomInstrumentRequest,
  ProductTypeEnum,
  SettleValueTypeEnum,
} from '@talos/kyoko';

export const CopyFromListedSymbol = {
  Yes: 'Yes',
  No: 'No',
} as const;
export type CopyFromListedSymbol = (typeof CopyFromListedSymbol)[keyof typeof CopyFromListedSymbol];

export const CustomSecurityMarket = {
  Internal: 'internal',
  InternalWhitelabel: 'internal_whitelabel',
} as const;
export type CustomSecurityMarket = (typeof CustomSecurityMarket)[keyof typeof CustomSecurityMarket];

export type CreateCustomSecurityStepId = 'setup' | 'instrumentDetails' | 'success';

export type CreateCustomInstrumentRequest = Pick<ICustomInstrumentRequest, 'RequestType' | 'Symbol' | 'ProductType'> &
  Partial<
    Omit<
      ICustomInstrumentRequest,
      'RequestID' | 'Timestamp' | 'User' | 'OrgID' | 'RequestType' | 'Symbol' | 'ProductType'
    >
  > & {
    ProductDetails?: {
      UnderlyingQuoteCurrency?: string;
      NotionalSize?: string;
    };
    DefaultSizeIncrement?: string;
    DefaultPriceIncrement?: string;
    PositionCurrency?: string;
    SettlementCurrency?: string;
    PriceDisplaySpec?: string;
    SizeDisplaySpec?: string;
  };

/** Fields that map 1:1 to `ICustomInstrumentRequest` (drawer values are strings). */
type CustomInstrumentRequestFormFields = Partial<
  Pick<
    ICustomInstrumentRequest,
    | 'ProductType'
    | 'Symbol'
    | 'DisplaySymbol'
    | 'Description'
    | 'QuoteCurrency'
    | 'BaseCurrency'
    | 'MinPriceIncrement'
    | 'MinSizeIncrement'
    | 'ContractSize'
    | 'StrikePrice'
    | 'OptionType'
  >
>;

/** Extra POST body fields (see `CreateCustomInstrumentRequest` intersection). */
type CustomInstrumentRequestExtensionFormFields = Partial<
  Pick<
    CreateCustomInstrumentRequest,
    | 'DefaultSizeIncrement'
    | 'DefaultPriceIncrement'
    | 'PositionCurrency'
    | 'SettlementCurrency'
    | 'PriceDisplaySpec'
    | 'SizeDisplaySpec'
  >
> & {
  SettleValueType?: SettleValueTypeEnum;
};

/** Workflow UI state — stripped or nested before POST. */
type CustomSecurityWorkflowOnlyFields = {
  CopyFromListedSymbol?: CopyFromListedSymbol;
  CopySourceSymbol?: string;
  NotionalSize?: string;
  UnderlyingQuoteCurrency?: string;
  /** Form control; serialized to `Expiration` on submit. */
  Expiry?: Date | null;
};

/** Same API field names, different form types than `ICustomInstrumentRequest`. */
type CustomSecurityFormFieldOverrides = {
  /** `ICustomInstrumentRequest.Market` is `JSON_Market`; the workflow uses market slug strings. */
  Market?: CustomSecurityMarket;
};

export type CreateCustomSecurityForm = CustomInstrumentRequestFormFields &
  CustomInstrumentRequestExtensionFormFields &
  CustomSecurityWorkflowOnlyFields &
  CustomSecurityFormFieldOverrides;

export interface CustomInstrumentCreateResult {
  status: CustomInstrumentRequestStatusEnum;
  timestamp?: string;
  symbol: string;
  productType: ProductTypeEnum;
}
